import { useCallback, useEffect } from 'react';
import loadable from '@loadable/component';

import { ManualPortfolioFeedbackBanner } from '@/features/portfolio/components/FeedbackBanner';

import { isIncompleteManualPortfolio } from '../PortfolioManual/utils';

import type { Holding, Portfolio } from '@/hooks/portfolio/usePortfolio';

import { usePanelHoldingId } from '../../hooks/usePanelHoldingId';

import { HoldingsOverviewLayout } from './HoldingsOverviewLayout';

const DemoPortfolioFAQ = loadable(() => import('../DemoPortfolioFAQ'), {
  resolveComponent: (c) => c.DemoPortfolioFAQ,
});
const DemoHoldingsTable = loadable(() => import('../HoldingsTable'), {
  resolveComponent: (c) => c.DemoHoldingsTable,
});
const PortfolioPanel = loadable(() => import('../PortfolioPanel'), {
  resolveComponent: (c) => c.PortfolioPanel,
});

type Props = {
  portfolio: Portfolio;
  excludedHoldings?: Holding[];
  sortedHoldings: Holding[];
};

export const DemoHoldingsOverview = ({ portfolio, sortedHoldings }: Props) => {
  const defaultHoldingId = sortedHoldings[0]?.id ?? null;

  const [panelHoldingId, setPanelHoldingId] = usePanelHoldingId({
    defaultId: defaultHoldingId,
  });

  const handleToggleTableExpansion = useCallback(() => {
    setPanelHoldingId((id) => (id ? null : defaultHoldingId));
  }, [defaultHoldingId, setPanelHoldingId]);

  useEffect(() => {
    if (
      portfolio.type === 'manual' &&
      isIncompleteManualPortfolio(sortedHoldings).incomplete
    ) {
      setPanelHoldingId(null);
    }
  }, [portfolio.id, sortedHoldings, portfolio.type, setPanelHoldingId]);

  return (
    <HoldingsOverviewLayout
      portfolio={portfolio}
      panelHoldingId={panelHoldingId}
      onToggleTableExpansion={handleToggleTableExpansion}
      main={
        <>
          <DemoHoldingsTable
            portfolioId={portfolio.id}
            excludedHoldings={[]}
            holdings={sortedHoldings}
            holdingId={panelHoldingId}
            onHoldingSelect={setPanelHoldingId}
          />
          <div className="px-x2 pt-x3 lg:px-0">
            <DemoPortfolioFAQ />
          </div>
          <div className="px-x2 pt-x1 lg:px-0">
            <ManualPortfolioFeedbackBanner />
          </div>
        </>
      }
      aside={
        panelHoldingId ? (
          <PortfolioPanel
            holdingId={panelHoldingId}
            excludedHoldings={[]}
            holdings={sortedHoldings}
            setHoldingId={setPanelHoldingId}
          />
        ) : null
      }
    />
  );
};
